import React, { useState, useMemo } from 'react';
import Tabs from '@salesforce/design-system-react/components/tabs';
import TabsPanel from '@salesforce/design-system-react/components/tabs/panel';
import Icon from '@salesforce/design-system-react/components/icon';
import { colors, type, space, radius, elevation, formatMoney, relativeDate, engagementLevel } from '../shared/tokens';
import sampleData from '../shared/sampleData';

import HouseholdRoster from '../shared/components/HouseholdRoster';
import ContributionShareBar from '../shared/components/ContributionShareBar';
import SeasonalAddressCard from '../shared/components/SeasonalAddressCard';
import KPIStrip from '../shared/components/KPIStrip';
import SectionCard from '../shared/components/SectionCard';
import UnifiedTimeline from '../shared/components/UnifiedTimeline';
import QuickActionBar from '../shared/components/QuickActionBar';

const SEGMENT_COLORS = ['#7F8DE1', '#06a59a', '#fe9339', '#9050e9'];

const HouseholdHero = ({ household, primary, members, roles, onMakePrimary }) => {
  const [primaryMenuOpen, setPrimaryMenuOpen] = useState(false);
  const avgEngagement = useMemo(() => {
    const scored = members.filter(m => m.engagementScore != null);
    if (scored.length === 0) return null;
    const avg = scored.reduce((s, m) => s + m.engagementScore, 0) / scored.length;
    return engagementLevel(Math.round(avg));
  }, [members]);

  return (
    <header style={{
      position: 'sticky',
      top: 0,
      zIndex: 10,
      background: colors.surface,
      borderBottom: `1px solid ${colors.border}`,
      padding: `${space[4]}px ${space[6]}px`,
      boxShadow: elevation.sticky,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: space[4], flexWrap: 'wrap' }}>
        <div style={{
          width: 64, height: 64, borderRadius: radius.lg,
          background: colors.brandMuted,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          <Icon category="standard" name="household" size="medium" style={{ fill: colors.brand }} />
        </div>
        <div style={{ flex: '1 1 auto', minWidth: 0 }}>
          <h1 style={{ ...type.h1, color: colors.textPrimary, margin: 0 }}>{household.name}</h1>
          <div style={{ display: 'flex', gap: space[4], flexWrap: 'wrap', marginTop: space[2], alignItems: 'center' }}>
            <div style={{ position: 'relative' }}>
              <button
                onClick={() => setPrimaryMenuOpen(!primaryMenuOpen)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: space[1],
                  padding: `${space[1]}px ${space[2]}px`,
                  background: colors.brandMuted,
                  color: colors.brand,
                  border: `1px solid ${colors.brand}`,
                  borderRadius: radius.pill,
                  ...type.small,
                  fontWeight: 700,
                  fontFamily: 'inherit',
                  cursor: 'pointer',
                }}
              >
                Primary: {primary.firstName} {primary.lastName}
                <Icon category="utility" name="chevrondown" size="xx-small" style={{ fill: colors.brand }} />
              </button>
              {primaryMenuOpen && (
                <div style={{
                  position: 'absolute',
                  top: 'calc(100% + 4px)', left: 0,
                  background: colors.surface,
                  border: `1px solid ${colors.border}`,
                  borderRadius: radius.md,
                  boxShadow: elevation.cardHover,
                  minWidth: 200, padding: space[1], zIndex: 30,
                }}>
                  {members.filter(m => m.id !== primary.id && roles[m.id] !== 'Child').map(m => (
                    <button
                      key={m.id}
                      onClick={() => { onMakePrimary(m.id); setPrimaryMenuOpen(false); }}
                      style={{
                        display: 'flex', width: '100%',
                        padding: `${space[2]}px ${space[3]}px`,
                        background: 'transparent', border: 'none',
                        ...type.small, color: colors.textPrimary,
                        fontFamily: 'inherit', cursor: 'pointer', textAlign: 'left',
                      }}
                      onMouseEnter={e => { e.currentTarget.style.background = colors.surfaceInk; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                    >
                      Re-assign to {m.firstName} {m.lastName}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {avgEngagement && (
              <div style={{ display: 'flex', alignItems: 'center', gap: space[1] }}>
                <span style={{ ...type.label, color: colors.textTertiary }}>Avg engagement</span>
                <span style={{
                  ...type.small, fontWeight: 700, color: avgEngagement.color,
                }}>
                  {avgEngagement.label}
                </span>
              </div>
            )}
          </div>
          <div style={{
            display: 'flex', gap: space[6], marginTop: space[3], flexWrap: 'wrap', ...type.small,
          }}>
            <div>
              <span style={{ ...type.label, color: colors.textTertiary, marginRight: space[1] }}>Formal</span>
              <span style={{ color: colors.textPrimary }}>{household.formalGreeting}</span>
            </div>
            <div>
              <span style={{ ...type.label, color: colors.textTertiary, marginRight: space[1] }}>Informal</span>
              <span style={{ color: colors.textPrimary }}>{household.informalGreeting}</span>
            </div>
          </div>
        </div>
        <div style={{ flexShrink: 0 }}>
          <QuickActionBar actions={['log-gift', 'email', 'task', 'note']} />
        </div>
      </div>
    </header>
  );
};

const HouseholdAccount = () => {
  const household = sampleData.household;
  const [primaryId, setPrimaryId] = useState(household.primaryContactId);

  const members = useMemo(
    () => household.memberIds
      .map(id => Object.values(sampleData.contacts).find(c => c.id === id))
      .filter(Boolean),
    [household.memberIds],
  );
  const primary = members.find(m => m.id === primaryId);
  const [selected, setSelected] = useState(primary?.id);

  const segments = members
    .filter(m => m.lifetimeGiving > 0)
    .map((m, i) => ({
      id: m.id,
      label: `${m.firstName} ${m.lastName}`,
      value: m.lifetimeGiving,
      valueLabel: formatMoney(m.lifetimeGiving, { compact: true }),
      color: SEGMENT_COLORS[i % SEGMENT_COLORS.length],
    }));

  const kpiTiles = [
    {
      id: 'hh-lifetime',
      label: 'Household lifetime',
      value: formatMoney(household.householdLifetime, { compact: true }),
      subValue: `since ${new Date(household.firstGiftDate).getFullYear()}`,
    },
    { id: 'hh-largest', label: 'Largest gift', value: formatMoney(household.householdLargestGift, { compact: true }) },
    {
      id: 'hh-year',
      label: 'This year',
      value: formatMoney(household.thisYearTotal, { compact: true }),
      delta: Math.round(((household.thisYearTotal - household.lastYearTotal) / Math.max(1, household.lastYearTotal)) * 100),
    },
    {
      id: 'hh-mrr',
      label: 'Active recurring',
      value: formatMoney(household.activeRecurringMonthly),
      unit: '/mo',
      color: colors.success,
    },
    { id: 'hh-softc', label: 'Soft credits', value: formatMoney(household.softCreditTotal, { compact: true }) },
    { id: 'hh-avg', label: 'Avg gift', value: formatMoney(household.avgGiftAmount, { compact: true }) },
  ];

  // Filter timeline to events that belong to household members
  const timelineEvents = sampleData.timelineEvents;

  return (
    <div style={{ minHeight: '100%', background: colors.surfaceInk }}>
      <HouseholdHero
        household={household}
        primary={primary}
        members={members}
        roles={household.roles}
        onMakePrimary={setPrimaryId}
      />

      <div style={{ padding: space[5] }}>
        <SectionCard title="Household roster" subtitle="Click a member to filter below · the chip at top re-assigns the primary contact">
          <HouseholdRoster
            members={members}
            roles={household.roles}
            primaryId={primaryId}
            selectedId={selected}
            onSelect={setSelected}
            onMakePrimary={setPrimaryId}
          />
        </SectionCard>

        <SectionCard title="Contribution share" subtitle="Who gave how much, rolled up across the household">
          <ContributionShareBar segments={segments} />
        </SectionCard>

        <KPIStrip tiles={kpiTiles} />

        <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: radius.md, overflow: 'hidden' }}>
          <Tabs variant="default" id="household-tabs">
            <TabsPanel label="Overview">
              <div style={{ padding: space[5] }}>
                <SectionCard title="Household activity">
                  <UnifiedTimeline events={timelineEvents} />
                </SectionCard>
              </div>
            </TabsPanel>
            <TabsPanel label="Giving">
              <div style={{ padding: space[5] }}>
                <SectionCard title="Gift history" subtitle="Grouped by household member">
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ textAlign: 'left', ...type.label, color: colors.textTertiary }}>
                          <th style={{ padding: space[2], borderBottom: `1px solid ${colors.border}` }}>Date</th>
                          <th style={{ padding: space[2], borderBottom: `1px solid ${colors.border}` }}>Amount</th>
                          <th style={{ padding: space[2], borderBottom: `1px solid ${colors.border}` }}>Given by</th>
                          <th style={{ padding: space[2], borderBottom: `1px solid ${colors.border}` }}>Campaign</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sampleData.giftHistory
                          .slice()
                          .sort((a, b) => new Date(b.date) - new Date(a.date))
                          .slice(0, 12)
                          .map(g => (
                            <tr key={g.id} style={{ ...type.small }}>
                              <td style={{ padding: space[2], borderBottom: `1px solid ${colors.border}`, color: colors.textSecondary }}>
                                {new Date(g.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                              </td>
                              <td style={{ padding: space[2], borderBottom: `1px solid ${colors.border}`, fontWeight: 700, color: colors.timeline.gift }}>
                                {formatMoney(g.amount)}
                              </td>
                              <td style={{ padding: space[2], borderBottom: `1px solid ${colors.border}`, color: colors.textPrimary }}>
                                {primary.firstName} {primary.lastName}
                              </td>
                              <td style={{ padding: space[2], borderBottom: `1px solid ${colors.border}`, color: colors.brand, cursor: 'pointer' }}>
                                {g.campaign}
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </SectionCard>
              </div>
            </TabsPanel>
            <TabsPanel label="Addresses">
              <div style={{ padding: space[5] }}>
                <SectionCard
                  title="Addresses"
                  subtitle="Seasonal addresses get automatically swapped based on today's date"
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: space[3] }}>
                    {household.addresses.map(a => (
                      <SeasonalAddressCard key={a.id} address={a} onMakeDefault={() => alert(`Demo: make ${a.label} primary`)} />
                    ))}
                  </div>
                </SectionCard>
              </div>
            </TabsPanel>
            <TabsPanel label="Recurring">
              <div style={{ padding: space[5] }}>
                <SectionCard title="Active recurring gifts" subtitle={`${formatMoney(household.activeRecurringMonthly)}/mo household total`}>
                  <div style={{
                    ...type.small, color: colors.textTertiary, lineHeight: 1.5,
                  }}>
                    Open the Recurring Donation mockup for the detailed schedule strip and
                    pause / upgrade affordances. (Household-scoped view is v2.)
                  </div>
                </SectionCard>
              </div>
            </TabsPanel>
            <TabsPanel label="Notes">
              <div style={{ padding: space[5] }}>
                <SectionCard title="Notes">
                  {timelineEvents.filter(e => e.type === 'note').map(n => (
                    <div key={n.id} style={{ padding: `${space[3]}px 0`, borderBottom: `1px dashed ${colors.border}` }}>
                      <div style={{ ...type.small, fontWeight: 600, color: colors.textPrimary }}>{n.title}</div>
                      <div style={{ ...type.small, color: colors.textSecondary, marginTop: 2 }}>{n.description}</div>
                      <div style={{ ...type.micro, color: colors.textTertiary, marginTop: 4 }}>
                        {relativeDate(n.date)} · {n.actor}
                      </div>
                    </div>
                  ))}
                </SectionCard>
              </div>
            </TabsPanel>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default HouseholdAccount;
